import React from "react";
import type { AnalyticsClient } from "./analyticsClient";
import { describeIssues, withEventValidation } from "./analyticsClient";
import { ANALYTICS_DEFAULT_PROPERTIES_SCHEMA } from "./events";
import type { AnalyticsApp, AnalyticsDefaultProperties } from "./events";
import { createFreshpaintAnalyticsClient } from "./freshpaintAnalyticsClient";
import { EMPTY_ANALYTICS_IDENTITY, toRootIdentityProperties } from "./identity";
import type { AnalyticsIdentity } from "./identity";
import { createNoopAnalyticsClient } from "./noopAnalyticsClient";
import { DEFAULT_PROPERTIES_SUBJECT, enforcePiiDenyList } from "./piiDenyList";

export interface AnalyticsProviderProps {
  children: React.ReactNode;
  /**
   * Which app is reporting. On every event — without it bcc and client traffic
   * is indistinguishable in a shared Mixpanel project.
   */
  app: AnalyticsApp;
  /**
   * True while a Beanz admin is impersonating a roaster.
   *
   * Impersonation replaces `currentVendor` in `VendorProvider`, so events fired
   * during an impersonated session are otherwise indistinguishable from the
   * roaster's own activity and land in their metrics.
   *
   * Required, not optional with a `false` default: forgetting to wire it is
   * indistinguishable from wiring it correctly, and silently contaminates
   * every metric that filters on it. An app without impersonation passes
   * `false` explicitly, which is a statement rather than an omission.
   */
  isImpersonating: boolean;
  /**
   * Who is acting and on whose account — `useAnalyticsIdentity()` builds it
   * from the vendor in context and the profile on the token.
   *
   * Its `distinctId` drives `identify()` and `reset()`; the rest becomes root
   * properties on every event. Omitted, the provider still works and still
   * sends: events carry `tenureBand: "unknown"` and no account attribution,
   * which is the honest description of a session that has not resolved.
   *
   * **Identity going away is what triggers `reset()`**, so the provider has to
   * outlive the session it measures. Mount it inside the auth and vendor
   * providers but above the routes — high enough that signing out changes this
   * prop rather than unmounting the provider, because an unmount resets
   * nothing and the next person on a shared roastery terminal inherits the
   * previous person's identity.
   */
  identity?: AnalyticsIdentity;
  /**
   * Pre-built client — a test and Storybook seam, **ignored in production**.
   *
   * The root properties are screened where the provider builds the transport,
   * because a client carries them inside itself and adds them below
   * `withEventValidation`. An injected client skips that build, so whatever
   * root properties it carries are invisible here: a production adapter that
   * added a runtime `UserEmail` would send it. Production therefore always
   * gets the provider's own transport, with a warning naming the prop.
   */
  client?: AnalyticsClient;
  /**
   * Set to `false` to force the no-op client regardless of configuration —
   * declined consent, or any other reason to stop sending.
   *
   * This exists because `freshpaintKey={hasConsent ? key : undefined}` does
   * NOT work: an undefined prop falls through to `REACT_APP_FRESHPAINT_KEY`,
   * so the natural-looking conditional would keep sending after consent was
   * declined. Opting out has to be explicit.
   */
  enabled?: boolean;
  /** Defaults to `REACT_APP_FRESHPAINT_KEY`; without it, nothing is sent. */
  freshpaintKey?: string;
  /** Defaults to `REACT_APP_ENVIRONMENT`. */
  environment?: string;
}

/**
 * Picks the transport and wraps it in validation.
 *
 * Without a Freshpaint key — tests, Storybook, local development, consent
 * declined — this is the no-op client. Validation is applied either way, so a
 * bad payload is caught in development even where nothing is being sent.
 */
const createAnalyticsTransport = (transportKey: string | undefined, defaultProperties: AnalyticsDefaultProperties): AnalyticsClient => {
  // The root properties are screened here because this is the only place they
  // can be: `buildPayload` in the transport spreads them into the payload root
  // *below* `withEventValidation`, so the event-payload guard never sees them.
  // They are half the payload Mixpanel receives, and the half sourced from the
  // Salesforce token — the object that supplies `role`, `region` and
  // `tenureBand` is the same one carrying `UserEmail`, `UserFirstName` and
  // `UserLastName`.
  //
  // Nothing can reach it through this provider's props today:
  // `toRootIdentityProperties` names the four fields it copies, and the schema
  // below strips unknown keys. Both are incidental — a `...claims` spread in
  // either place removes the protection with no signal — and neither says
  // anything when it happens. This makes "no PII in the root" a stated
  // invariant at the boundary rather than an emergent property of two
  // unrelated decisions.
  //
  // Before the schema, for the reason an event payload is screened before its
  // schema: Zod strips unknown keys, so a smuggled `UserEmail` is dropped
  // silently and nobody learns the identity wiring is carrying it.
  //
  // Before the transport choice, so local development catches it. Nothing is
  // sent without a Freshpaint key, but the defect still has to surface where
  // the person who can fix it is working.
  //
  // Once per transport, not per event: these are built once per session, so a
  // wiring defect is named once instead of on every event for the rest of it.
  const screened = enforcePiiDenyList(DEFAULT_PROPERTIES_SUBJECT, defaultProperties);

  const result = ANALYTICS_DEFAULT_PROPERTIES_SCHEMA.safeParse(screened);
  if (!result.success) {
    // Fail closed — including when the screen above stripped a root property
    // and left a required one missing, which is the correct outcome: identity
    // wiring that carries PII should stop the session's analytics, not send
    // half of it.
    //
    // Sending with the root properties dropped would produce
    // events that cannot be attributed to an app, an environment, a vendor, a
    // region or a tenure, and cannot be excluded from reports when
    // impersonated — worse than sending nothing, because it quietly pollutes
    // every number. Account attribution rides here rather than on a Mixpanel
    // group entity, so losing it loses the account entirely.
    console.warn(`[analytics] sending nothing — invalid default properties: ${describeIssues(result.error)}`);
    return createNoopAnalyticsClient();
  }

  return transportKey ? createFreshpaintAnalyticsClient({ defaultProperties: result.data }) : createNoopAnalyticsClient();
};

/**
 * Default context value: a validating no-op client, so a component rendered
 * outside the provider can call `track` without guarding.
 */
export const AnalyticsContext = React.createContext<AnalyticsClient>(withEventValidation(createNoopAnalyticsClient()));

export const AnalyticsProvider = ({
  children,
  app,
  isImpersonating,
  client,
  enabled = true,
  freshpaintKey,
  environment,
  identity = EMPTY_ANALYTICS_IDENTITY,
}: AnalyticsProviderProps) => {
  // `enabled` gates the environment fallback too. Without that,
  // `freshpaintKey={hasConsent ? key : undefined}` would keep sending after
  // consent was declined, because the undefined prop falls through to the env.
  const resolvedKey = enabled ? (freshpaintKey ?? process.env.REACT_APP_FRESHPAINT_KEY) : undefined;
  const resolvedEnvironment = environment ?? process.env.REACT_APP_ENVIRONMENT ?? "unknown";
  // Dropped rather than trusted: see the prop. The same `NODE_ENV` switch the
  // deny-list uses, which every workspace's build already sets.
  const isProduction = process.env.NODE_ENV === "production";
  const injectedClient = isProduction ? undefined : client;

  React.useEffect(() => {
    if (isProduction && client) {
      console.warn("[analytics] ignoring the injected `client` prop in production — its root properties cannot be screened");
    }
  }, [isProduction, client]);

  // Memoised on the identity's own fields, not the identity object: a caller
  // passing an inline object would otherwise rebuild the transport on every
  // render.
  const { vendorKey, region, role, tenureBand } = identity;
  const rootIdentity = React.useMemo(
    () => toRootIdentityProperties({ vendorKey, region, role, tenureBand }),
    [vendorKey, region, role, tenureBand]
  );

  // The transport is stateless — no queue, no timer, nothing to dispose — so a
  // plain memo is enough and there is no cleanup for Strict Mode to trip over.
  // `enabled` outranks an injected client: a caller passing both a stub and
  // `enabled={false}` stops sending rather than emitting through the stub.
  //
  // The identity is spread, never reassembled key by key: an absent region has
  // to stay an absent key rather than reappear holding `undefined`.
  const transport = React.useMemo(
    () =>
      enabled && !injectedClient
        ? createAnalyticsTransport(resolvedKey, { app, environment: resolvedEnvironment, isImpersonating, ...rootIdentity })
        : createNoopAnalyticsClient(),
    [enabled, injectedClient, resolvedKey, app, resolvedEnvironment, isImpersonating, rootIdentity]
  );

  const analytics = React.useMemo(
    // The injected client is wrapped too. A stub that skipped validation would
    // let tests and stories pass payloads the real provider would drop.
    () => withEventValidation(enabled ? (injectedClient ?? transport) : createNoopAnalyticsClient()),
    [enabled, injectedClient, transport]
  );

  useIdentityLifecycle(analytics, identity.distinctId, enabled);

  return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>;
};

/**
 * Calls `identify()` when a session begins and `reset()` when it ends.
 *
 * Driven by the distinct id alone, not by the client: Freshpaint's identity is
 * global state on `window.freshpaint`, so re-running `identify` because the
 * root properties changed — the vendor resolving a beat after the contact id —
 * would be a no-op call with no payload to carry.
 *
 * The ref, not a cleanup function, is what makes this correct. A cleanup-based
 * reset fires on every dependency change and on unmount, so Strict Mode's
 * double-invoke would reset a live session; comparing against the last id
 * applied means the second invoke is a no-op and only a genuine change acts.
 *
 * A change of user — sign out then in, or an admin session handed over — is
 * reset-then-identify rather than a bare `identify`, so nothing from the
 * previous session is carried into the next one.
 *
 * **A layout effect, not a passive one, and that is load-bearing.** React runs
 * every layout effect before any passive effect, but passive effects go
 * child-before-parent. A descendant that tracks from its own mount effect —
 * page-view instrumentation is exactly that shape — would therefore reach the
 * transport before a passive effect here had run: on a cold anonymous load its
 * first event would be attributed to whoever used the browser last, and on a
 * freshly authenticated load it would go out under the anonymous id. Running
 * in the layout phase puts the identity in place before any descendant's mount
 * effect can fire. A descendant that tracked from its own *layout* effect
 * would still race, which is a boundary worth knowing rather than one worth
 * defending against.
 *
 * Nothing is applied — and nothing is recorded as applied — while `enabled` is
 * false. Advancing the ref against the no-op client would make a later consent
 * grant a silent no-op: the id would already read as applied, the equality
 * guard would return early, and the real transport would never be identified.
 * Skipping instead means the first render after consent identifies properly.
 * Note the inverse is a known limit: withdrawing consent mid-session does not
 * reset the SDK's stored identity, because by then the client is the no-op one
 * and the reset lands nowhere.
 */
const useIdentityLifecycle = (analytics: AnalyticsClient, distinctId: string | undefined, enabled: boolean) => {
  // `hasRun` is tracked separately from the id, not folded into it. Freshpaint
  // persists distinct_id in localStorage, so it outlives this ref: a cold load
  // after someone else's session ended finds both the ref and the id
  // undefined, and a guard that only compared ids would skip the reset and
  // leave the previous person's identity live on the SDK. The next
  // `identify()` would then land on top of it.
  const applied = React.useRef<{ hasRun: boolean; distinctId: string | undefined }>({ hasRun: false, distinctId: undefined });

  React.useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const previous = applied.current;
    if (previous.hasRun && previous.distinctId === distinctId) {
      return;
    }

    // Two things call for a reset, and the first is the one a ref alone misses:
    //   - a first mount with no session, which may be inheriting a persisted
    //     identity from whoever used this browser last;
    //   - a sign-out or a handover from a user we did identify.
    // A first mount that already has a session is not one of them: reaching it
    // means this browser's own tokens were restored, so the identity being
    // re-applied is the same person's.
    const mountedAnonymous = !previous.hasRun && !distinctId;
    const leftAKnownUser = previous.hasRun && previous.distinctId !== undefined;
    if (mountedAnonymous || leftAKnownUser) {
      analytics.reset();
    }

    if (distinctId) {
      analytics.identify(distinctId);
    }

    applied.current = { hasRun: true, distinctId };
  }, [analytics, distinctId, enabled]);
};
